import { useCallback, useState, useEffect, useMemo } from 'react';
import AccountService from 'views/CRM/Account/services/accountService.js';

const formatColumnName = (name) => {
  return name.replace(/([A-Z])/g, '_$1').toLowerCase();
};

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @typedef {Object} Account
 * @property {string} id
 * @property {string} name
 * @property {string} phone
 * @property {string} createdAt
 * @property {string} createdByName
 * @property {string} lastUpdatedAt
 * @property {string} recordTypeId
 */

/**
 * Hook customizado para gerenciar a lista de contas.
 * @param {Object} params
 * @param {string} params.recordTypeId
 * @param {SortConfig} params.sortConfig
 * @returns {{
 *   accounts: Account[] | null,
 *   totalAccounts: number,
 *   isLoadingAccounts: boolean,
 *   getAccounts: (page?: number, params?: Object) => void,
 *   clearAccounts: () => void
 * }}
 */
export const useAccounts = ({
  recordTypeId,
  sortConfig = { direction: null, key: null }
}) => {
  /** @type {[Account[], React.Dispatch<React.SetStateAction<Account[]>>]} */
  const [accounts, setAccounts] = useState([]);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  /**
   * Todas as contas como uma SelectableOption
   * @typedef {Object} SelectableOption
   * @property {string} label
   * @property {string} value
   */
  /** @type {SelectableOption[]} */
  const accountsOptions = useMemo(() => {
    return accounts.map((account) => ({
      label: account.name,
      value: account.id
    }));
  }, [accounts]);

  /**
   * Busca contas do servidor.
   * @param {number} [page=1]
   * @param {Object} [params={}]
   */
  const getAccounts = useCallback(
    (page, limit, params = {}) => {
      setIsLoadingAccounts(true);

      const orderType =
        sortConfig?.direction === null ||
        sortConfig?.direction === undefined ||
        // Algumas páginas estão passando `ascending` enquanto outras passam `ascendant`. Para evitar modificar muitos arquivos, aceita-se ambos os modos.
        sortConfig?.direction === 'ascending' ||
        sortConfig?.direction === 'ascendant'
          ? 'ASC'
          : 'DESC';

      const orderBy = sortConfig?.key
        ? formatColumnName(sortConfig?.key)
        : sortConfig?.key;

      AccountService.getAllAccounts(
        recordTypeId,
        { ...params, orderType, orderBy },
        {
          page,
          limit
        }
      )
        .then((response) => {
          setAccounts(response.data.data);
          setTotalAccounts(response.data.totalRegisters);
        })
        .finally(() => setIsLoadingAccounts(false));
    },
    [recordTypeId, JSON.stringify(sortConfig)]
  );

  /**
   * Limpa os dados das contas.
   */
  const clearAccounts = useCallback(() => {
    setAccounts(null);
    setTotalAccounts(0);
    setIsLoadingAccounts(false);
  }, []);

  useEffect(() => {
    if (sortConfig?.key) {
      getAccounts();
    }
  }, [getAccounts, JSON.stringify(sortConfig)]);

  return {
    accounts,
    totalAccounts,
    isLoadingAccounts,
    getAccounts,
    clearAccounts,
    accountsOptions
  };
};
